import { memo, useMemo, useRef, useState } from "react";
import { Panel } from "primereact/panel";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import { useReport } from "../../context/report";

const METRICAS = [
    { label: "Trama", value: "trama" },
    { label: "Urdume", value: "urdume" },
    { label: "Trama + Urdume", value: "total" },
];

const CLASSIFICACOES = [
    { label: "Crítico", value: "CRÍTICO" },
    { label: "Atenção", value: "ATENÇÃO" },
    { label: "Pontual", value: "PONTUAL" },
];

const PESO_CLASSE = {
    "CRÍTICO": 3,
    "ATENÇÃO": 2,
    "PONTUAL": 1,
};

const ESTILO_CLASSE = {
    "CRÍTICO": { background: "#ffc7ce", color: "#9c0006", fontWeight: 700 },
    "ATENÇÃO": { background: "#ffeb9c", color: "#9c5700", fontWeight: 700 },
    "PONTUAL": { color: "#000", fontWeight: 500 },
};

// Classificação pela soma de quebras:
// abaixo de "atencaoMin" = PONTUAL | de "atencaoMin" até "criticoMax" = ATENÇÃO | acima de "criticoMax" = CRÍTICO
const classificar = (soma, atencaoMin, criticoMax) => {
    const max = criticoMax === null || criticoMax === undefined ? Infinity : criticoMax;
    const min = atencaoMin === null || atencaoMin === undefined ? 0 : atencaoMin;
    if (soma > max) return "CRÍTICO";
    if (soma >= min) return "ATENÇÃO";
    return "PONTUAL";
};

const compararLinhas = (a, b, campo, direcao) => {
    let res = 0;
    if (campo === "tear") {
        const numA = Number(a.tear);
        const numB = Number(b.tear);
        if (!isNaN(numA) && !isNaN(numB)) {
            res = numA - numB;
        } else {
            res = String(a.tear).localeCompare(String(b.tear), "pt-BR", { numeric: true });
        }
    } else if (campo === "artigo") {
        res = a.artigo.localeCompare(b.artigo, "pt-BR", { numeric: true });
    } else if (campo === "turnos") {
        res = a.turnos - b.turnos;
    } else if (campo === "pct") {
        res = a.pct - b.pct;
    } else if (campo === "soma") {
        res = a.soma - b.soma;
    } else if (campo === "media") {
        res = a.media - b.media;
    } else if (campo === "pico") {
        res = a.pico - b.pico;
    } else if (campo === "classe") {
        const pesoA = PESO_CLASSE[a.classe] || 0;
        const pesoB = PESO_CLASSE[b.classe] || 0;
        res = pesoA - pesoB;
    }

    if (res === 0) {
        // Desempate estável
        res = Number(a.tear) - Number(b.tear) || a.artigo.localeCompare(b.artigo, "pt-BR");
    }

    return direcao === "desc" ? -res : res;
};

const fmt1 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmt0 = new Intl.NumberFormat("pt-BR");

export default memo(function QuebrasResumoArtigo() {
    const { data } = useReport();

    const [metrica, setMetrica] = useState("trama");
    const [limite, setLimite] = useState(15);
    const [artigos, setArtigos] = useState([]);
    const [classes, setClasses] = useState([]);
    const [atencaoMin, setAtencaoMin] = useState(100);
    const [criticoMax, setCriticoMax] = useState(250);

    const [sortField, setSortField] = useState("turnos");
    const [sortOrder, setSortOrder] = useState("desc");

    const tabelaRef = useRef(null);
    const [printing, setPrinting] = useState(false);

    const handlePrint = useReactToPrint({
        content: () => tabelaRef.current,
        pageStyle: "@page { size: A4 portrait; margin: 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }",
        onBeforeGetContent: () =>
            new Promise((resolve) => {
                setPrinting(true);
                setTimeout(resolve, 500);
            }),
        onAfterPrint: () => setPrinting(false),
    });

    // Registros do backend (sem as linhas "Total")
    const registros = useMemo(() => {
        if (!data?.datatable) return [];
        const lista = [];
        data.datatable.forEach((r) => {
            if (r.turma === "Total") return;
            lista.push({
                tear: r.tear,
                artigo: r.artigo === null || r.artigo === undefined || r.artigo === "" ? "-" : String(r.artigo),
                turno: `${String(r.data).trim()}|${r.turma}`,
                trama: Number(r.trama) || 0,
                urdume: Number(r.urdume) || 0,
            });
        });
        return lista;
    }, [data]);

    const artigoOptions = useMemo(
        () =>
            [...new Set(registros.map((r) => r.artigo))]
                .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
                .map((a) => ({ label: a, value: a })),
        [registros]
    );

    const { linhas, totalTurnos, maxTurnos } = useMemo(() => {
        // Total de turnos (dia + turma) existentes no período
        const total = new Set(registros.map((r) => r.turno)).size;
        const corte = limite === null || limite === undefined ? 0 : limite;

        // soma os registros de cada tear + artigo + turno
        const mapa = new Map();
        registros.forEach((r) => {
            if (artigos.length > 0 && !artigos.includes(r.artigo)) return;
            const chave = `${r.tear}|${r.artigo}`;
            if (!mapa.has(chave)) mapa.set(chave, { tear: r.tear, artigo: r.artigo, turnos: {} });
            const t = mapa.get(chave).turnos;
            if (!t[r.turno]) t[r.turno] = { trama: 0, urdume: 0 };
            t[r.turno].trama += r.trama;
            t[r.turno].urdume += r.urdume;
        });

        let lista = [];
        mapa.forEach((item) => {
            // Cada métrica é avaliada separadamente contra o limite.
            // Em "Trama + Urdume", soma-se apenas o que passou no limite de cada uma.
            const ocorrencias = [];
            Object.values(item.turnos).forEach((t) => {
                const tramaOk = t.trama >= corte;
                const urdumeOk = t.urdume >= corte;
                if (metrica === "trama") {
                    if (tramaOk) ocorrencias.push(t.trama);
                } else if (metrica === "urdume") {
                    if (urdumeOk) ocorrencias.push(t.urdume);
                } else if (tramaOk || urdumeOk) {
                    ocorrencias.push((tramaOk ? t.trama : 0) + (urdumeOk ? t.urdume : 0));
                }
            });
            if (ocorrencias.length === 0) return;
            const soma = ocorrencias.reduce((a, b) => a + b, 0);
            const pico = Math.max(...ocorrencias);
            const pct = total > 0 ? (ocorrencias.length / total) * 100 : 0;
            lista.push({
                tear: item.tear,
                artigo: item.artigo,
                turnos: ocorrencias.length,
                pct,
                soma,
                media: soma / ocorrencias.length,
                pico,
                classe: classificar(soma, atencaoMin, criticoMax),
            });
        });

        if (classes.length > 0) lista = lista.filter((l) => classes.includes(l.classe));

        lista.sort((a, b) => compararLinhas(a, b, sortField, sortOrder));

        return {
            linhas: lista,
            totalTurnos: total,
            maxTurnos: lista.reduce((m, l) => Math.max(m, l.turnos), 0),
        };
    }, [registros, artigos, metrica, limite, classes, atencaoMin, criticoMax, sortField, sortOrder]);

    const handleSort = (campo) => {
        if (sortField === campo) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(campo);
            const iniciaAsc = campo === "tear" || campo === "artigo";
            setSortOrder(iniciaAsc ? "asc" : "desc");
        }
    };

    const renderSortIcon = (campo) => {
        if (sortField !== campo) {
            return <i className="pi pi-sort-alt qra-sort-icon qra-sort-idle" />;
        }
        return sortOrder === "asc" ? (
            <i className="pi pi-sort-amount-up-alt qra-sort-icon qra-sort-active" />
        ) : (
            <i className="pi pi-sort-amount-down qra-sort-icon qra-sort-active" />
        );
    };

    const limpar = () => {
        setArtigos([]);
        setClasses([]);
        setMetrica("trama");
        setLimite(15);
        setAtencaoMin(100);
        setCriticoMax(250);
        setSortField("turnos");
        setSortOrder("desc");
    };

    // degradê vermelho proporcional ao nº de turnos
    const corTurnos = (v) => {
        const r = maxTurnos > 0 ? v / maxTurnos : 0;
        const g = 255 - Math.round(135 * r);
        return `rgb(255, ${g}, ${g})`;
    };

    return (
        <Panel header="Resumo de quebras por tear e artigo" toggleable collapsed={true}>
            <style>{`
                .qra-wrap { overflow-x: auto; }
                .qra-wrap.qra-print { overflow: visible; }
                .qra-table, .qra-table * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .qra-table { border-collapse: collapse; font-size: 13px; width: 100%; min-width: 640px; }
                .qra-table th, .qra-table td { border: 1px solid #d0d0d0; padding: 5px 8px; text-align: center; white-space: nowrap; }
                .qra-table thead th { background: #1f3864; color: #fff; font-weight: 700; white-space: normal; }
                .qra-legenda { font-size: 12px; color: #444; margin-top: 8px; }
                .qra-th-sort { cursor: pointer; user-select: none; transition: background 0.15s ease; }
                .qra-th-sort:hover { background: #2b4c80 !important; }
                .qra-th-content { display: inline-flex; align-items: center; justify-content: center; gap: 6px; width: 100%; }
                .qra-sort-icon { font-size: 11px; }
                .qra-sort-idle { opacity: 0.35; }
                .qra-th-sort:hover .qra-sort-idle { opacity: 0.8; }
                .qra-sort-active { color: #ffeb9c; opacity: 1; font-weight: bold; }
            `}</style>

            <div className="p-fluid p-formgrid p-grid p-mb-2">
                <div className="p-field p-col-12 p-md-3">
                    <label>Artigo</label>
                    <MultiSelect
                        value={artigos}
                        options={artigoOptions}
                        onChange={(e) => setArtigos(e.value)}
                        placeholder="Todos os artigos"
                        display="chip"
                        filter
                    />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>Ocorrência com quebras ≥</label>
                    <InputNumber
                        value={limite}
                        onValueChange={(e) => setLimite(e.value)}
                        placeholder="Quantidade de quebras"
                        min={0}
                        showButtons
                    />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>Atenção a partir de (soma)</label>
                    <InputNumber
                        value={atencaoMin}
                        onValueChange={(e) => setAtencaoMin(e.value)}
                        placeholder="Soma mínima"
                        min={0}
                        showButtons
                    />
                </div>
                <div className="p-field p-col-12 p-md-3">
                    <label>Crítico acima de (soma)</label>
                    <InputNumber
                        value={criticoMax}
                        onValueChange={(e) => setCriticoMax(e.value)}
                        placeholder="Soma máxima"
                        min={0}
                        showButtons
                    />
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap p-mb-3">
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Métrica:</span>
                    <SelectButton value={metrica} options={METRICAS} onChange={(e) => e.value && setMetrica(e.value)} unselectable={false} />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2" style={{ minWidth: "240px" }}>
                    <span className="p-mr-2" style={{ fontWeight: 600 }}>Classificação:</span>
                    <MultiSelect
                        value={classes}
                        options={CLASSIFICACOES}
                        onChange={(e) => setClasses(e.value)}
                        placeholder="Todas"
                        display="chip"
                        style={{ flex: 1 }}
                    />
                </div>
                <div className="p-d-flex p-ai-center p-mb-2">
                    <Button
                        className="p-button-outlined p-mr-2"
                        label="Limpar filtros"
                        icon="pi pi-filter-slash"
                        onClick={limpar}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                    <Button
                        label="Imprimir"
                        icon="pi pi-print"
                        onClick={handlePrint}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                </div>
            </div>

            <div ref={tabelaRef} style={{ padding: printing ? "20px" : 0 }}>
                {printing && (
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                        Resumo de quebras por tear e artigo — {data?.periodo}
                    </h2>
                )}

                {linhas.length === 0 ? (
                    <p>Nenhum tear encontrado com os filtros selecionados.</p>
                ) : (
                    <>
                        <div className={`qra-wrap ${printing ? "qra-print" : ""}`}>
                            <table className="qra-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort("tear")} className="qra-th-sort" title="Clique para ordenar por Tear">
                                            <div className="qra-th-content">
                                                <span>Tear</span>
                                                {renderSortIcon("tear")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("artigo")} className="qra-th-sort" title="Clique para ordenar por Artigo">
                                            <div className="qra-th-content">
                                                <span>Artigo</span>
                                                {renderSortIcon("artigo")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("turnos")} className="qra-th-sort" title="Clique para ordenar por Turnos c/ ocorrência">
                                            <div className="qra-th-content">
                                                <span>Turnos c/ ocorrência</span>
                                                {renderSortIcon("turnos")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("pct")} className="qra-th-sort" title="Clique para ordenar por % dos turnos">
                                            <div className="qra-th-content">
                                                <span>% dos turnos</span>
                                                {renderSortIcon("pct")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("soma")} className="qra-th-sort" title="Clique para ordenar por Soma quebras">
                                            <div className="qra-th-content">
                                                <span>Soma quebras</span>
                                                {renderSortIcon("soma")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("media")} className="qra-th-sort" title="Clique para ordenar por Média por ocorrência">
                                            <div className="qra-th-content">
                                                <span>Média por ocorrência</span>
                                                {renderSortIcon("media")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("pico")} className="qra-th-sort" title="Clique para ordenar por Pico">
                                            <div className="qra-th-content">
                                                <span>Pico</span>
                                                {renderSortIcon("pico")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("classe")} className="qra-th-sort" title="Clique para ordenar por Classificação">
                                            <div className="qra-th-content">
                                                <span>Classificação</span>
                                                {renderSortIcon("classe")}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhas.map((l, i) => (
                                        <tr key={`${l.tear}|${l.artigo}`}>
                                            <td>{l.tear}</td>
                                            <td>{l.artigo}</td>
                                            <td style={{ background: corTurnos(l.turnos) }}>{l.turnos}</td>
                                            <td>{Math.round(l.pct)}%</td>
                                            <td>{fmt0.format(l.soma)}</td>
                                            <td>{fmt1.format(l.media)}</td>
                                            <td>{fmt0.format(l.pico)}</td>
                                            <td style={ESTILO_CLASSE[l.classe]}>{l.classe}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="qra-legenda">
                            Ocorrência: turno com {limite ?? 0} quebras ou mais, avaliando trama e urdume separadamente
                            {metrica === "total" ? " (soma apenas o que atingiu o limite em cada uma)" : ` (${METRICAS.find((m) => m.value === metrica).label.toLowerCase()})`}.
                            Total de turnos no período: {totalTurnos}.
                            Classificação pela soma de quebras: pontual abaixo de {atencaoMin ?? 0};
                            atenção de {atencaoMin ?? 0} até {criticoMax ?? "∞"}; crítico acima de {criticoMax ?? "∞"}.
                        </div>
                    </>
                )}
            </div>
        </Panel>
    );
});